import {
  all,
  takeLatest,
  takeEvery,
  fork,
  call,
  put,
  select,
  cancel,
} from 'redux-saga/effects';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import {
  getStorage,
  ref as sRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import {
  getFirestore,
  doc,
  getDocFromServer,
} from 'firebase/firestore';
import { notification } from 'antd';
import { v1 as uuidv1 } from 'uuid';
import * as Sentry from '@sentry/react';
import actions from './actions';
import authActions from '../auth/actions';

const ROOT_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_ROOT_URL;

const AI_PROCESSING_TIMEOUT_MS = 560000; // ~9min20s

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function getAiRecorderPermissionsFromDB(mainUser) {
  const fs = getFirestore();
  let uid;
  if (mainUser) {
    uid = mainUser;
  } else {
    const auth = getAuth();
    const { currentUser } = auth;
    ({ uid } = currentUser);
  }
  const userRef = doc(
    fs,
    'professionals',
    uid,
    'aiRecorder',
    'users',
  );
  return getDocFromServer(userRef);
}

const PREFERRED_MIC_KEY = 'preferredMicrophoneId';
const getSelectedMicrophoneId = (state) => state.AiRecorder.selectedMicrophoneId;
const getRecorderState = (state) => state.AiRecorder;
const getUserId = (state) => state.Auth.user?.uid;
const getMaxRecordingTime = (state) => state.AiRecorder.maxRecordingTime;
const getUnifiedToken = (state) => state.Auth.unified;
const getSelectedAddressFromStore = (state) => state.App.selectedAddress;
const getMainUserFromStore = (state) => state.Auth.mainUser;
const getAudioData = (state) => state.AiRecorder.audioData;
const getRecordingTime = (state) => state.AiRecorder.recordingTime;
const getAiRecorderPermissionsState = (state) => state.AiRecorder.permissions;
const getPendingAudioBlob = (state) => state.AiRecorder.pendingAudioBlob;
const getRecordFieldMode = (state) => state.AiRecorder.recordFieldMode;
const getTranscriptionFiles = (state) => state.AiRecorder.transcriptionFiles;

let mediaRecorder = null;
let stream = null;
let recordingStartTime = null;
let timerInterval = null;
let totalElapsedTime = 0;
let pauseStartTime = null;
let audioChunks = [];
let isFinalizing = false;

function generateRecordingId() {
  return `recording_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Helper to cleanup recording resources
function cleanupRecording() {
  if (mediaRecorder) {
    if (mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    mediaRecorder = null;
  }
  if (stream) {
    stream.getTracks().forEach((track) => track.stop());
    stream = null;
  }
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
  recordingStartTime = null;
  totalElapsedTime = 0;
  pauseStartTime = null;
  audioChunks = [];
}

function getPreferredMicrophoneId() {
  return localStorage.getItem(PREFERRED_MIC_KEY);
}

function savePreferredMicrophoneId(microphoneId) {
  if (microphoneId) {
    localStorage.setItem(PREFERRED_MIC_KEY, microphoneId);
  }
}

const PREFERRED_POSITION_KEY = 'preferredWidgetPosition';
const WIDGET_POSITIONS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

function getPreferredWidgetPosition() {
  const saved = localStorage.getItem(PREFERRED_POSITION_KEY);
  return WIDGET_POSITIONS.includes(saved) ? saved : 'bottom-left';
}

function savePreferredWidgetPosition(position) {
  if (WIDGET_POSITIONS.includes(position)) {
    localStorage.setItem(PREFERRED_POSITION_KEY, position);
  }
}

function* getValidMicrophone(preferredId) {
  try {
    const devices = yield call([navigator.mediaDevices, 'enumerateDevices']);
    const microphones = devices.filter((device) => device.kind === 'audioinput');

    // If preferred microphone is available, use it
    if (preferredId && microphones.find((mic) => mic.deviceId === preferredId)) {
      return preferredId;
    }

    // Fallback to first available microphone
    return microphones[0]?.deviceId || null;
  } catch (error) {
    console.error('Error getting valid microphone:', error);
    return null;
  }
}

// Get microphone stream
function* getMicrophoneStream(microphoneId, sampleRate = 44100) {
  try {
    const constraints = {
      audio: {
        deviceId: microphoneId ? { exact: microphoneId } : undefined,
        echoCancellation: true,
        noiseSuppression: true,
        sampleRate,
      },
    };
    const micStream = yield call([navigator.mediaDevices, 'getUserMedia'], constraints);
    return micStream;
  } catch (error) {
    console.error('Failed to get microphone stream:', error);
    throw error;
  }
}

function* startTimer() {
  if (timerInterval) {
    clearInterval(timerInterval);
  }
  timerInterval = setInterval(() => {
    // This will be handled by the saga that calls startTimer
  }, 1000);

  const maxRecordingTime = yield select(getMaxRecordingTime);

  while (timerInterval && recordingStartTime && !pauseStartTime) {
    const currentElapsed = (Date.now() - recordingStartTime) / 1000;
    const totalTime = totalElapsedTime + currentElapsed;
    const totalTimeSeconds = Math.floor(totalTime);
    const timeRemaining = maxRecordingTime - totalTime;

    yield put({
      type: actions.AI_RECORDER_UPDATE_RECORDING_TIME,
      payload: { time: totalTimeSeconds },
    });

    if (timeRemaining <= 15) {
      yield put({
        type: actions.AI_RECORDER_SET_TIME_WARNING,
        payload: {
          isWarning: timeRemaining > 0,
          timeRemaining: Math.ceil(Math.max(0, timeRemaining)),
        },
      });
    } else {
      yield put({
        type: actions.AI_RECORDER_SET_TIME_WARNING,
        payload: {
          isWarning: false,
          timeRemaining: Math.ceil(timeRemaining),
        },
      });
    }

    if (totalTimeSeconds > 0
      && totalTimeSeconds % 20 === 0
      && audioChunks.length === 0) {
      notification.warning({
        message: 'Microfone sem áudio',
        description:
          'Nenhum áudio está sendo capturado. Verifique se o microfone está ligado e não está silenciado.',
        placement: 'topRight',
        duration: 5,
      });
    }

    if (totalTime >= maxRecordingTime) {
      console.info('Max recording time reached, pausing recording...');
      yield put({ type: actions.AI_RECORDER_TIME_LIMIT_REACHED });
      yield put({ type: actions.AI_RECORDER_PAUSE_RECORDING });
      break;
    }

    yield call(sleep, 1000);
  }
}

// Server/API functions (preserved from original)
function getIdToken() {
  const auth = getAuth();
  const { currentUser } = auth;
  return currentUser.getIdToken();
}

function processAudioOnCloud(audioUrl, mimeType, idToken, useAlternativeTranscription = false, audioGsUri = null, audioDurationSeconds = null) {
  const config = {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
    timeout: AI_PROCESSING_TIMEOUT_MS, // ~9min20s, acima do limite de 540s da função
  };
  console.info(audioUrl, mimeType);
  return axios.post(
    `${ROOT_URL}/aiMedicalProcessor`,
    {
      // duração só para debug no backend (correlacionar áudio curto/longo com tokens e falhas)
      audioUrl, audioGsUri, mimeType, useAlternativeTranscription, audioDurationSeconds,
    },
    config,
  );
}

function regenerateReportOnCloud(transcription, idToken) {
  const config = {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
    timeout: AI_PROCESSING_TIMEOUT_MS, // ~9min20s, acima do limite de 540s da função
  };
  return axios.post(
    `${ROOT_URL}/aiMedicalProcessor`,
    { transcription },
    config,
  );
}

// Saga functions
function* fetchMicrophonesRequest() {
  yield takeLatest(actions.AI_RECORDER_FETCH_MICROPHONES, function* () {
    try {
      const devices = yield call([navigator.mediaDevices, 'enumerateDevices']);
      const microphones = devices.filter((device) => device.kind === 'audioinput');
      yield put({
        type: actions.AI_RECORDER_FETCH_MICROPHONES_SUCCESS,
        payload: { microphones },
      });
    } catch (error) {
      console.warn('Error fetching microphones:', error);
      yield put({
        type: actions.AI_RECORDER_FETCH_MICROPHONES_FAILURE,
        payload: { error: error.message },
      });
    }
  });
}

function* startRecordingRequest() {
  yield takeLatest(actions.AI_RECORDER_START_RECORDING, function* (action) {
    const userId = yield select(getUserId);
    console.info('[AI Recorder] optimized recording: true | sampleRate: 16000 | audioBitsPerSecond: 32000 | timeslice: 10000ms');
    const permissions = yield select(getAiRecorderPermissionsState);
    if (!permissions || !permissions[userId]) {
      notification.error({
        message: 'Acesso Negado',
        description: 'Você não tem permissão para usar a funcionalidade de Transcrição Médica por IA.',
        placement: 'topRight',
      });
      return;
    }

    const { microphoneId: requestedMicrophoneId } = action.payload || {};
    let selectedMicId = requestedMicrophoneId || (yield select(getSelectedMicrophoneId));

    try {
      cleanupRecording();
      if (!requestedMicrophoneId) {
        const preferredMicId = getPreferredMicrophoneId();
        const validMicId = yield call(getValidMicrophone, preferredMicId);

        if (validMicId) {
          selectedMicId = validMicId;
          // Update Redux state if we're using a different microphone
          if (validMicId !== (yield select(getSelectedMicrophoneId))) {
            yield put({
              type: actions.AI_RECORDER_CHANGE_MICROPHONE_SUCCESS,
              payload: { microphoneId: validMicId },
            });
          }
        }
      }

      const recordingId = generateRecordingId();

      stream = yield call(getMicrophoneStream, selectedMicId, 16000);

      const supportedTypes = [
        'audio/webm',
        'audio/wav',
        'audio/mp3',
        'audio/mp4',
        'audio/mpeg',
      ];

      const mimeType = supportedTypes.find((type) => MediaRecorder.isTypeSupported(type)) || 'audio/webm';

      mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 32000,
      });
      audioChunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.start(10000);
      recordingStartTime = Date.now();
      totalElapsedTime = 0;
      pauseStartTime = null;

      yield fork(startTimer);

      yield put({
        type: actions.AI_RECORDER_START_RECORDING_SUCCESS,
        payload: { recordingId, mimeType },
      });

      notification.success({
        message: 'Gravação Iniciada',
        description: 'Gravação de áudio iniciada para geração de relatório médico',
        placement: 'topRight',
        duration: 2,
      });
    } catch (error) {
      console.error('Error starting recording:', error);
      cleanupRecording();

      // Try fallback with default microphone if preferred mic failed
      if (!requestedMicrophoneId && selectedMicId !== null) {
        try {
          console.info('Trying fallback with default microphone...');
          const fallbackStream = yield call(getMicrophoneStream, null, 16000);

          if (fallbackStream) {
            stream = fallbackStream;
            const supportedTypes = [
              'audio/webm',
              'audio/wav',
              'audio/mp3',
              'audio/mp4',
              'audio/mpeg',
            ];
            const mimeType = supportedTypes.find((type) => MediaRecorder.isTypeSupported(type)) || 'audio/webm';

            mediaRecorder = new MediaRecorder(stream, {
              mimeType,
              audioBitsPerSecond: 32000,
            });
            audioChunks = [];

            mediaRecorder.ondataavailable = (event) => {
              if (event.data.size > 0) {
                audioChunks.push(event.data);
              }
            };

            mediaRecorder.start(10000);
            recordingStartTime = Date.now();
            totalElapsedTime = 0;
            pauseStartTime = null;

            yield fork(startTimer);

            yield put({
              type: actions.AI_RECORDER_START_RECORDING_SUCCESS,
              payload: { recordingId: generateRecordingId(), mimeType },
            });

            notification.warning({
              message: 'Microfone Alternativo',
              description: 'Usando microfone padrão. O microfone preferido não está disponível.',
              placement: 'topRight',
              duration: 3,
            });

            return;
          }
        } catch (fallbackError) {
          console.error('Fallback microphone also failed:', fallbackError);
        }
      }

      yield put({
        type: actions.AI_RECORDER_START_RECORDING_FAILURE,
        payload: { error: error.message },
      });

      notification.error({
        message: 'Erro na Gravação',
        description: 'Falha ao acessar microfone. Verifique as permissões e tente novamente.',
        placement: 'topRight',
      });
    }
  });
}

function* pauseRecordingRequest() {
  yield takeEvery(actions.AI_RECORDER_PAUSE_RECORDING, function* () {
    try {
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.pause();
        if (timerInterval) {
          clearInterval(timerInterval);
          timerInterval = null;
        }
        totalElapsedTime += (Date.now() - recordingStartTime) / 1000;
        pauseStartTime = Date.now();

        yield put({ type: actions.AI_RECORDER_PAUSE_RECORDING_SUCCESS });
        notification.info({
          message: 'Gravação Pausada',
          description: 'A gravação foi pausada. Clique em "Continuar" para retomar.',
          placement: 'topRight',
          duration: 2,
        });
      }
    } catch (error) {
      console.error('Error pausing recording:', error);
      notification.error({
        message: 'Erro ao Pausar',
        description: 'Falha ao pausar a gravação. Tente novamente.',
        placement: 'topRight',
        duration: 3,
      });
    }
  });
}

function* resumeRecordingRequest() {
  yield takeEvery(actions.AI_RECORDER_RESUME_RECORDING, function* () {
    try {
      if (mediaRecorder && mediaRecorder.state === 'paused') {
        mediaRecorder.resume();
        recordingStartTime = Date.now();
        pauseStartTime = null;
        yield fork(startTimer);

        yield put({ type: actions.AI_RECORDER_RESUME_RECORDING_SUCCESS });
        notification.success({
          message: 'Gravação Retomada',
          description: 'A gravação foi retomada.',
          placement: 'topRight',
          duration: 2,
        });
      }
    } catch (error) {
      console.error('Error resuming recording:', error);
      notification.error({
        message: 'Erro ao Retomar',
        description: 'Falha ao retomar a gravação. Tente novamente.',
        placement: 'topRight',
        duration: 3,
      });
    }
  });
}

function* deleteRecordingRequest() {
  yield takeEvery(actions.AI_RECORDER_DELETE_RECORDING, function* () {
    try {
      cleanupRecording();

      yield put({ type: actions.AI_RECORDER_DELETE_RECORDING_SUCCESS });
      notification.warning({
        message: 'Gravação Excluída',
        description: 'A gravação foi excluída e todo o áudio foi descartado.',
        placement: 'topRight',
        duration: 2,
      });
    } catch (error) {
      console.error('Error deleting recording:', error);
      notification.error({
        message: 'Erro ao Excluir',
        description: 'Falha ao excluir a gravação. Tente novamente.',
        placement: 'topRight',
        duration: 3,
      });
    }
  });
}

function* finalizeRecordingRequest() {
  yield takeLatest(actions.AI_RECORDER_FINALIZE_RECORDING, function* () {
    if (isFinalizing) return;
    isFinalizing = true;
    try {
      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        return;
      }

      const currentMimeType = mediaRecorder.mimeType || 'audio/webm';
      const stopPromise = new Promise((resolve) => {
        mediaRecorder.onstop = () => {
          if (audioChunks.length > 0) {
            const audioBlob = new Blob(audioChunks, {
              type: currentMimeType,
            });
            if (audioBlob.size > 0) {
              resolve(audioBlob);
            } else {
              resolve(null);
            }
          } else {
            resolve(null);
          }
        };
      });

      if (mediaRecorder.state === 'paused') {
        mediaRecorder.resume();
      }
      mediaRecorder.stop();

      const audioBlob = yield call(() => stopPromise);

      cleanupRecording();

      if (!audioBlob || audioBlob.size === 0) {
        yield put({ type: actions.AI_RECORDER_DELETE_RECORDING_SUCCESS });
        notification.warning({
          message: 'Áudio Não Detectado',
          description:
            'Nenhum áudio foi capturado. Verifique se o microfone está funcionando corretamente.',
          placement: 'topRight',
          duration: 5,
        });
        return;
      }

      const duration = yield select(getRecordingTime);

      let audioUrl = null;
      try {
        audioUrl = URL.createObjectURL(audioBlob);
      } catch (error) {
        console.error('Failed to create audio URL:', error);
        throw new Error('Failed to create audio URL for playback');
      }

      const audioData = {
        url: audioUrl,
        type: audioBlob.type,
        size: audioBlob.size,
        recordingId: generateRecordingId(),
        blob: audioBlob,
        duration,
      };

      yield put({
        type: actions.AI_RECORDER_FINALIZE_RECORDING_SUCCESS,
        payload: { audioData },
      });

      yield put({
        type: actions.AI_RECORDER_OPEN_MODAL,
      });

      yield put({
        type: actions.AI_RECORDER_PROCESS_AUDIO,
        payload: { audioBlob, recordingId: audioData.recordingId },
      });
    } catch (error) {
      console.warn(error);
      cleanupRecording();
      yield put({ type: actions.AI_RECORDER_DELETE_RECORDING_SUCCESS });
      Sentry.captureException(error, {
        tags: { feature: 'ai-recorder' },
        contexts: { operation: { name: 'finalize-recording' } },
      });
      notification.error({
        message: 'Erro ao Finalizar',
        description: 'Falha ao finalizar a gravação. Tente novamente.',
        placement: 'topRight',
        duration: 3,
      });
    } finally {
      isFinalizing = false;
    }
  });
}

function* loadWidgetPositionRequest() {
  yield takeLatest(actions.AI_RECORDER_LOAD_WIDGET_POSITION, function* () {
    const position = yield call(getPreferredWidgetPosition);
    yield put({
      type: actions.AI_RECORDER_CHANGE_WIDGET_POSITION_SUCCESS,
      payload: { position },
    });
  });
}

function* changeWidgetPositionRequest() {
  yield takeLatest(actions.AI_RECORDER_CHANGE_WIDGET_POSITION, function* (action) {
    const { position } = action.payload;
    if (!WIDGET_POSITIONS.includes(position)) return;
    yield call(savePreferredWidgetPosition, position);
    yield put({
      type: actions.AI_RECORDER_CHANGE_WIDGET_POSITION_SUCCESS,
      payload: { position },
    });
  });
}

function* changeMicrophoneRequest() {
  yield takeLatest(actions.AI_RECORDER_CHANGE_MICROPHONE, function* (action) {
    try {
      const { microphoneId } = action.payload;
      const wasRecording = mediaRecorder && mediaRecorder.state === 'recording';
      const wasPaused = mediaRecorder && mediaRecorder.state === 'paused';
      const currentMimeType = mediaRecorder?.mimeType || 'audio/webm';

      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      stream = yield call(getMicrophoneStream, microphoneId, 16000);

      if ((wasRecording || wasPaused) && audioChunks) {
        const supportedTypes = [
          'audio/webm',
          'audio/wav',
          'audio/mp3',
          'audio/mp4',
          'audio/mpeg',
        ];
        const mimeType = supportedTypes.find((type) => MediaRecorder.isTypeSupported(type)) || 'audio/webm';

        const newMediaRecorder = new MediaRecorder(stream, {
          mimeType: currentMimeType && MediaRecorder.isTypeSupported(currentMimeType) ? currentMimeType : mimeType,
          audioBitsPerSecond: 32000,
        });

        newMediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunks.push(event.data);
          }
        };

        mediaRecorder = newMediaRecorder;
        mediaRecorder.start(10000);

        if (wasPaused) {
          mediaRecorder.pause();
        }
      }

      savePreferredMicrophoneId(microphoneId);

      yield put({
        type: actions.AI_RECORDER_CHANGE_MICROPHONE_SUCCESS,
        payload: { microphoneId },
      });

      const updatedRecorderState = yield select(getRecorderState);
      const selectedMic = updatedRecorderState.availableMicrophones?.find((mic) => mic.deviceId === microphoneId);
      notification.success({
        message: 'Microfone Alterado',
        description: `Microfone alterado para: ${selectedMic?.label || 'Microfone sem nome'}`,
        placement: 'topRight',
        duration: 2,
      });
    } catch (error) {
      console.error('Error changing microphone:', error);
      yield put({
        type: actions.AI_RECORDER_CHANGE_MICROPHONE_FAILURE,
        payload: { error: error.message },
      });
      notification.error({
        message: 'Erro ao Alterar Microfone',
        description: 'Falha ao acessar o novo microfone.',
        placement: 'topRight',
      });
    }
  });
}

function uploadAudioBytes(storageRef, audioBlob, mimeType) {
  return uploadBytes(storageRef, audioBlob, {
    contentType: mimeType,
  });
}

function getAudioDownloadURL(ref) {
  return getDownloadURL(ref);
}

function* uploadAudioToStorage(audioBlob, mimeType, fileName, unified, addressUid, mainUser) {
  const storage = getStorage();
  const auth = getAuth();
  const { currentUser } = auth;

  let uid;
  if (mainUser) {
    uid = mainUser;
  } else {
    ({ uid } = currentUser);
  }

  let storagePath = '';

  if (unified?.id && unified?.address?.some((a) => a === addressUid)) {
    storagePath = `unified/${unified.id}/audio/${fileName}`;
  } else {
    storagePath = `users/${uid}/audio/${fileName}`;
  }

  const storageRef = sRef(storage, storagePath);

  const snapshot = yield call(uploadAudioBytes, storageRef, audioBlob, mimeType);

  console.info('Audio uploaded successfully, path:', storagePath);
  const downloadURL = yield call(getAudioDownloadURL, snapshot.ref);
  console.info('Download URL obtained:', downloadURL);

  // Generate gs:// URL for Vertex AI
  const bucketName = storage.app.options.storageBucket;
  const gsUrl = `gs://${bucketName}/${storagePath}`;
  console.info('GS URL for Vertex AI:', gsUrl);

  return { downloadURL, gsUrl, storagePath };
}

function isTimeoutError(error) {
  return error.code === 'ECONNABORTED'
    || error.code === 'ETIMEDOUT'
    || error.message?.includes('timeout');
}

function isNetworkError(error) {
  return error.code === 'ERR_NETWORK'
    || error.message?.includes('Network Error')
    || error.message?.includes('ECONNREFUSED')
    || !navigator.onLine;
}

function isServerError(error) {
  const status = error.response?.status;
  return status >= 500 && status < 600;
}

function* hasAiRecorderPermission(deniedDescription = 'Você não tem permissão para processar áudio com IA.') {
  const userId = yield select(getUserId);
  const permissions = yield select(getAiRecorderPermissionsState);
  if (!permissions || !permissions[userId]) {
    notification.error({
      message: 'Acesso Negado',
      description: deniedDescription,
      placement: 'topRight',
    });
    return false;
  }
  return true;
}

function* processAudioRequest() {
  yield takeLatest(actions.AI_RECORDER_PROCESS_AUDIO, function* (action) {
    if (!(yield call(hasAiRecorderPermission))) {
      return;
    }

    try {
      const { audioBlob, useAlternativeTranscription } = action.payload;

      let mimeType = audioBlob.type || 'audio/webm';
      if (mimeType.includes('webm')) {
        mimeType = 'audio/webm';
      } else if (mimeType.includes('wav')) {
        mimeType = 'audio/wav';
      } else if (mimeType.includes('mp3')) {
        mimeType = 'audio/mp3';
      } else if (mimeType.includes('mp4')) {
        mimeType = 'audio/mp4';
      } else if (mimeType.includes('mpeg')) {
        mimeType = 'audio/mpeg';
      }

      yield put({
        type: actions.AI_RECORDER_SET_PROCESSING_STEP,
        payload: { progress: 0 },
      });

      const unified = yield select(getUnifiedToken);
      const addressUid = yield select(getSelectedAddressFromStore);
      const mainUser = yield select(getMainUserFromStore);
      const audioData = yield select(getAudioData);
      const recordingTime = yield select(getRecordingTime);

      let audioDurationSeconds = audioData?.duration || recordingTime;

      if (!audioDurationSeconds) {
        audioDurationSeconds = audioBlob.size / ((192 * 1024) / 8);
      }

      const uploadProgressTask = yield fork(function* () {
        for (let i = 0; i <= 15; i += 1) {
          yield call(sleep, 666);
          const progress = Math.min(30, i * 2);
          yield put({
            type: actions.AI_RECORDER_SET_PROCESSING_STEP,
            payload: { progress },
          });
        }
      });

      const timestamp = Date.now();
      const fileExtension = mimeType.split('/')[1] || 'webm';
      const fileName = `Gravação atendimento  ${timestamp}.${fileExtension}`;
      const uploadStart = Date.now();
      const { downloadURL, gsUrl, storagePath } = yield call(
        uploadAudioToStorage,
        audioBlob,
        mimeType,
        fileName,
        unified,
        addressUid,
        mainUser,
      );
      console.info('[timing] audio upload ms:', Date.now() - uploadStart, '| size bytes:', audioBlob.size);

      yield cancel(uploadProgressTask);
      yield put({
        type: actions.AI_RECORDER_SET_PROCESSING_STEP,
        payload: { progress: 30 },
      });

      const idToken = yield call(getIdToken);

      const estimatedProcessingTime = Math.max(10, audioDurationSeconds / 30);
      const progressTask = yield fork(function* () {
        const totalSteps = 65;
        const delayPerStep = (estimatedProcessingTime * 1000) / totalSteps;
        for (let i = 0; i < totalSteps; i += 1) {
          yield call(sleep, delayPerStep);
          const totalProgress = Math.min(95, 30 + i + 1);
          yield put({
            type: actions.AI_RECORDER_SET_PROCESSING_STEP,
            payload: { progress: totalProgress },
          });
        }
      });

      console.info('Processing audio, GS URL:', gsUrl);

      const cloudStart = Date.now();
      const { data } = yield call(
        processAudioOnCloud,
        downloadURL,
        mimeType,
        idToken,
        useAlternativeTranscription,
        gsUrl,
        audioDurationSeconds,
      );
      console.info('[timing] cloud processing ms:', Date.now() - cloudStart);

      yield cancel(progressTask);

      yield put({
        type: actions.AI_RECORDER_SET_PROCESSING_STEP,
        payload: { progress: 100 },
      });
      const recordFieldMode = yield select(getRecordFieldMode);
      if (data.audioError) {
        const audioErrorMessage = 'Não foi possível detectar fala no áudio. Verifique se o microfone está funcionando corretamente e tente novamente.';
        yield put({
          type: actions.AI_RECORDER_PROCESS_AUDIO_SUCCESS,
          payload: {
            transcription: audioErrorMessage,
            report: '',
            transcriptionFiles: {
              downloadURL,
              gsUrl,
              fileName,
              type: fileExtension,
              uid: uuidv1(),
              recordFieldMode: recordFieldMode || '',
              date: timestamp,
              duration: audioDurationSeconds,
              storagePath,
            },
          },
        });
        return;
      }

      if (data.success) {
        const { transcription, report } = data;
        const finalTranscription = transcription || 'Transcrição não disponível';
        const finalReport = report || '';

        yield put({
          type: actions.AI_RECORDER_PROCESS_AUDIO_SUCCESS,
          payload: {
            transcription: finalTranscription,
            report: finalReport,
            transcriptionFiles: {
              downloadURL,
              gsUrl,
              fileName,
              type: fileExtension,
              uid: uuidv1(),
              recordFieldMode: recordFieldMode || '',
              date: timestamp,
              duration: audioDurationSeconds,
              storagePath,
            },
          },
        });

        if (data.reportError && transcription) {
          // Laudo falhou no servidor: mantém a transcrição e regenera o laudo
          yield put({
            type: actions.AI_RECORDER_REGENERATE_REPORT,
            payload: { transcription: finalTranscription },
          });
        }
      } else {
        throw new Error(data.error || 'Processing failed');
      }
    } catch (error) {
      // Log detalhado para debug
      console.error('[DEBUG] Process audio error:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        online: navigator.onLine,
      });

      const timedOut = isTimeoutError(error);
      const networkFailure = isNetworkError(error);

      // Offline confirmado é a conexão do usuário, não falha da plataforma.
      if (!networkFailure || navigator.onLine) {
        Sentry.captureException(error, {
          level: timedOut || networkFailure ? 'warning' : 'error',
          tags: {
            feature: 'ai-recorder',
            ...(timedOut ? { errorType: 'timeout' } : {}),
            ...(networkFailure && !timedOut ? { errorType: 'network' } : {}),
          },
          contexts: {
            operation: { name: 'process-audio-cloud' },
            upload: {
              online: navigator.onLine,
              connection: navigator.connection?.effectiveType || 'unknown',
              status: error.response?.status || null,
              code: error.code || null,
            },
          },
          fingerprint: [
            'ai-recorder',
            'process-audio-cloud',
            // eslint-disable-next-line no-nested-ternary
            timedOut ? 'timeout' : (networkFailure ? 'network' : 'unknown'),
          ],
        });
      }

      if (timedOut) {
        // Processamento da IA passou do tempo: mostra modal com botão de retry
        yield put({
          type: actions.AI_RECORDER_PROCESS_AUDIO_FAILURE,
          payload: {
            errorType: 'timeout',
            error: 'O processamento da IA demorou mais que o esperado. Isso costuma acontecer com áudios muito longos. Tente novamente ou grave um trecho menor.',
          },
        });
      } else if (isNetworkError(error)) {
        // Erro de rede: mostra modal com botão de retry
        yield put({
          type: actions.AI_RECORDER_PROCESS_AUDIO_FAILURE,
          payload: {
            errorType: 'network',
            error: 'Erro de conexão. Verifique sua internet e tente novamente.',
          },
        });
      } else if (isServerError(error)) {
        // Erro do servidor (5xx): mostra modal com botão de retry
        yield put({
          type: actions.AI_RECORDER_PROCESS_AUDIO_FAILURE,
          payload: {
            errorType: 'network',
            error: 'Houve uma instabilidade no servidor ao processar o áudio. Tente novamente.',
          },
        });
      } else {
        // Outros erros: mostra na tela de transcrição como mensagem
        const audioErrorMessage = 'Houve um problema ao processar o áudio. Verifique se o microfone está funcionando corretamente e tente novamente.';
        yield put({
          type: actions.AI_RECORDER_PROCESS_AUDIO_SUCCESS,
          payload: {
            transcription: audioErrorMessage,
            report: '',
            transcriptionFiles: null,
          },
        });
      }
    }
  });
}

function* showHelpModalRequest() {
  yield takeLatest(actions.AI_RECORDER_SHOW_HELP_MODAL, function* () {
    try {
      const hideHelp = localStorage.getItem('/aiRecorder/saga');
      if (hideHelp === 'hideRecordingHelp') {
        yield put({ type: actions.AI_RECORDER_SHOW_HELP_MODAL_CLOSE });
        yield put({ type: actions.AI_RECORDER_OPEN_TEST_MODAL });
      }
    } catch (error) {
      console.error('Error showing help modal:', error);
    }
  });
}

function* dontShowHelpAgainRequest() {
  yield takeLatest(actions.AI_RECORDER_DONT_SHOW_HELP_AGAIN, function* () {
    try {
      localStorage.setItem('/aiRecorder/saga', 'hideRecordingHelp');
      yield put({ type: actions.AI_RECORDER_SHOW_HELP_MODAL_CLOSE });
      yield put({ type: actions.AI_RECORDER_OPEN_TEST_MODAL });
    } catch (error) {
      console.error('Error handling dont show help again:', error);
    }
  });
}

export function* getAiRecorderPermissions() {
  yield takeEvery([
    authActions.LOGIN_SUCCESS,
  ], function* () {
    try {
      const mainUser = yield select(getMainUserFromStore);
      const aiRecorderPermissions = yield call(getAiRecorderPermissionsFromDB, mainUser);
      if (aiRecorderPermissions.data()) {
        yield put({
          type: actions.AI_RECORDER_GET_PERMISSION_SUCCESS,
          payload: {
            permissions: {
              ...aiRecorderPermissions.data(),
            },
          },
        });
      } else {
        throw new Error('No AI Recorder data');
      }
    } catch (error) {
      console.warn('AI Recorder permissions error:', error.message);
    }
  });
}

function* retryProcessAudioRequest() {
  yield takeLatest(actions.AI_RECORDER_RETRY_PROCESS_AUDIO, function* () {
    const pendingAudioBlob = yield select(getPendingAudioBlob);

    if (!pendingAudioBlob) {
      notification.error({
        message: 'Erro ao Tentar Novamente',
        description: 'Não foi possível encontrar o áudio para reprocessar. Por favor, faça uma nova gravação.',
        placement: 'topRight',
      });
      yield put({
        type: actions.AI_RECORDER_PROCESS_AUDIO_FAILURE,
        payload: {
          errorType: 'audio',
          error: 'Áudio não encontrado para reprocessamento.',
        },
      });
      return;
    }

    // Dispara o processo de áudio novamente com o blob salvo
    yield put({
      type: actions.AI_RECORDER_PROCESS_AUDIO,
      payload: { audioBlob: pendingAudioBlob },
    });
  });
}

function* retranscribeAudioRequest() {
  yield takeLatest(actions.AI_RECORDER_RETRANSCRIBE_AUDIO, function* () {
    if (!(yield call(hasAiRecorderPermission))) {
      yield put({
        type: actions.AI_RECORDER_PROCESS_AUDIO_FAILURE,
        payload: { error: 'Sem permissão para reprocessar.', errorType: null },
      });
      return;
    }

    const transcriptionFiles = yield select(getTranscriptionFiles);
    const audioData = yield select(getAudioData);
    const downloadURL = transcriptionFiles?.downloadURL;
    if (!downloadURL) {
      if (audioData?.blob) {
        yield put({
          type: actions.AI_RECORDER_PROCESS_AUDIO,
          payload: { audioBlob: audioData.blob, useAlternativeTranscription: true },
        });
      } else {
        notification.error({
          message: 'Áudio Indisponível',
          description: 'Não foi possível localizar o áudio para reprocessar. Faça uma nova gravação.',
          placement: 'topRight',
        });
        yield put({
          type: actions.AI_RECORDER_PROCESS_AUDIO_FAILURE,
          payload: { error: 'Áudio não encontrado para reprocessamento.', errorType: null },
        });
      }
      return;
    }

    const mimeType = transcriptionFiles.type ? `audio/${transcriptionFiles.type}` : 'audio/webm';
    const audioDurationSeconds = transcriptionFiles.duration || audioData?.duration || 60;
    const storage = getStorage();
    const bucketName = storage.app.options.storageBucket;
    const audioGsUri = transcriptionFiles.gsUrl
      || (transcriptionFiles.storagePath ? `gs://${bucketName}/${transcriptionFiles.storagePath}` : null);

    const progressTask = yield fork(function* () {
      const totalSteps = 90;
      const estimatedProcessingTime = Math.max(10, audioDurationSeconds / 30);
      const delayPerStep = (estimatedProcessingTime * 1000) / totalSteps;
      for (let i = 0; i < totalSteps; i += 1) {
        yield call(sleep, delayPerStep);
        yield put({
          type: actions.AI_RECORDER_SET_PROCESSING_STEP,
          payload: { progress: Math.min(95, i + 1) },
        });
      }
    });

    try {
      const idToken = yield call(getIdToken);

      const cloudStart = Date.now();
      const { data } = yield call(
        processAudioOnCloud,
        downloadURL,
        mimeType,
        idToken,
        true,
        audioGsUri,
        audioDurationSeconds,
      );
      console.info('[timing] retranscribe cloud processing ms:', Date.now() - cloudStart);

      yield cancel(progressTask);
      yield put({
        type: actions.AI_RECORDER_SET_PROCESSING_STEP,
        payload: { progress: 100 },
      });

      if (data.audioError) {
        yield put({
          type: actions.AI_RECORDER_PROCESS_AUDIO_SUCCESS,
          payload: {
            transcription: 'Não foi possível detectar fala no áudio. Verifique se o microfone está funcionando corretamente e tente novamente.',
            report: '',
            transcriptionFiles,
          },
        });
        return;
      }

      if (data.success) {
        yield put({
          type: actions.AI_RECORDER_PROCESS_AUDIO_SUCCESS,
          payload: {
            transcription: data.transcription || 'Transcrição não disponível',
            report: data.report || '',
            transcriptionFiles,
          },
        });

        notification.success({
          message: 'Transcrição Reprocessada',
          description: 'A transcrição foi reprocessada com uma IA alternativa.',
          placement: 'topRight',
        });
      } else {
        throw new Error(data.error || 'Retranscription failed');
      }
    } catch (error) {
      yield cancel(progressTask);

      const timedOut = isTimeoutError(error);
      const networkFailure = isNetworkError(error);

      // Offline confirmado é a conexão do usuário, não falha da plataforma.
      if (!networkFailure || navigator.onLine) {
        Sentry.captureException(error, {
          level: timedOut || networkFailure ? 'warning' : 'error',
          tags: {
            feature: 'ai-recorder',
            ...(timedOut ? { errorType: 'timeout' } : {}),
            ...(networkFailure && !timedOut ? { errorType: 'network' } : {}),
          },
          contexts: {
            operation: { name: 'retranscribe-audio-cloud' },
            upload: {
              online: navigator.onLine,
              connection: navigator.connection?.effectiveType || 'unknown',
              status: error.response?.status || null,
              code: error.code || null,
            },
          },
          fingerprint: [
            'ai-recorder',
            'retranscribe-audio-cloud',
            // eslint-disable-next-line no-nested-ternary
            timedOut ? 'timeout' : (networkFailure ? 'network' : 'unknown'),
          ],
        });
      }

      notification.error({
        message: 'Erro ao Reprocessar',
        description: isNetworkError(error)
          ? 'Erro de conexão. Verifique sua internet e tente novamente.'
          : 'Não foi possível reprocessar a transcrição. Tente novamente.',
        placement: 'topRight',
      });

      // Keep the existing transcription visible (errorType null avoids the
      // network error screen, so the user can simply click to retry).
      yield put({
        type: actions.AI_RECORDER_PROCESS_AUDIO_FAILURE,
        payload: { error: 'Falha ao reprocessar a transcrição.', errorType: null },
      });
    }
  });
}

function* regenerateReportRequest() {
  yield takeLatest(actions.AI_RECORDER_REGENERATE_REPORT, function* (action) {
    if (!(yield call(hasAiRecorderPermission, 'Você não tem permissão para regenerar relatórios com IA.'))) {
      return;
    }

    try {
      const { transcription } = action.payload;

      if (!transcription || transcription.trim() === '') {
        throw new Error('Transcrição vazia. Não é possível gerar relatório.');
      }

      const idToken = yield call(getIdToken);

      const response = yield call(
        regenerateReportOnCloud,
        transcription.trim(),
        idToken,
      );
      const { data } = response;

      if (data.success) {
        const { report } = data;

        if (!report) {
          throw new Error('No report received from the API');
        }

        yield put({
          type: actions.AI_RECORDER_PROCESS_AUDIO_SUCCESS,
          payload: { transcription, report },
        });

        notification.success({
          message: 'Relatório Regenerado',
          description: 'Relatório médico foi regenerado com sucesso!',
          placement: 'topRight',
        });
      } else {
        throw new Error(data.error || 'Report regeneration failed');
      }
    } catch (error) {
      let errorMessage = 'Erro ao regenerar relatório médico';
      if (error.response?.status === 401) {
        errorMessage = 'Erro de autenticação. Faça login novamente.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Acesso negado. Verifique suas permissões.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Erro no servidor. Tente novamente em alguns minutos.';
      } else if (error.message) {
        errorMessage = `Erro ao regenerar relatório: ${error.message}`;
      }

      yield put({
        type: actions.AI_RECORDER_PROCESS_AUDIO_FAILURE,
        payload: { error: errorMessage },
      });

      notification.error({
        message: 'Erro na Regeneração',
        description: errorMessage,
        placement: 'topRight',
      });
    }
  });
}

export default function* rootSaga() {
  yield all([
    fork(fetchMicrophonesRequest),
    fork(startRecordingRequest),
    fork(pauseRecordingRequest),
    fork(resumeRecordingRequest),
    fork(deleteRecordingRequest),
    fork(finalizeRecordingRequest),
    fork(changeMicrophoneRequest),
    fork(loadWidgetPositionRequest),
    fork(changeWidgetPositionRequest),
    fork(processAudioRequest),
    fork(retryProcessAudioRequest),
    fork(retranscribeAudioRequest),
    fork(regenerateReportRequest),
    fork(showHelpModalRequest),
    fork(dontShowHelpAgainRequest),
    fork(getAiRecorderPermissions),
  ]);
}

// Debug utilities (simplified)
export const debugUtils = {
  getRecordingState: () => ({
    hasMediaRecorder: !!mediaRecorder,
    hasStream: !!stream,
    audioChunksCount: audioChunks.length,
    recordingStartTime,
    getMediaRecorder: () => mediaRecorder,
  }),
  cleanup: cleanupRecording,
};
